import { Form } from "antd";
import { CARRIER_METHOD, SHIPPING_TYPE } from "features/Cart/constants";
import { getShippingTypeOptions } from "features/Cart/shippingPolicy";
import { useGetShippingProviderLazy, useGetShippingTypes } from "hooks/cart";
import { useChangeShippingMethod } from "hooks/order";
import { t } from "i18next";
import { useEffect, useMemo, useState } from "react";
import { notify } from "utils/helperFuncs";

/**
 * State + submit cho pop up đổi PTVC/ĐVVC. KHÔNG đụng vào dữ liệu đơn cho tới khi bấm [Xác nhận] —
 * khác `AdditionalDeliver` ở màn giỏ hàng (lưu ngay mỗi onBlur/onChange). Mọi lựa chọn ở đây chỉ
 * nằm trong form + state cục bộ, gom lại gửi 1 lần khi confirm.
 */
const useModalChangeShippingMethod = ({ order, visible, onClose, onRefetch }) => {
  const [form] = Form.useForm();
  const [shippingType, setShippingType] = useState();
  const [selectedPickup, setSelectedPickup] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const { handleChangeShippingMethod } = useChangeShippingMethod();

  // BE tự tính khả dụng theo zone + ngưỡng giá trị đơn. Chỉ gọi khi popup mở — component này luôn
  // mounted sẵn trong DeliveryMethod dù popup đang ẩn. `network-only` để mở lại popup luôn có data
  // mới nhất mà không cần tự `refetch()` thủ công.
  const { data: shippingTypes } = useGetShippingTypes({
    contactCityID: order?.receiverContact?.cityID,
    contactWardID: order?.receiverContact?.wardID,
    exportedWarehouseID: order?.exportedWarehouseID,
    subTotal: order?.subTotal,
    skip: !visible,
    fetchPolicy: "network-only",
  });

  // Chỉ PTVC "Chuyển phát"/"Giao hàng hoả tốc" mới cần chọn ĐVVC — BUS/PICK_UP_COUNTER/URBAN_* dùng
  // field khác hẳn. Không cần gọi API ĐVVC ngay khi mở popup nếu PTVC đang chọn chưa cần đến nó.
  const needsProviderOptions =
    shippingType === SHIPPING_TYPE.DELIVER_3PARTY || shippingType === SHIPPING_TYPE.DELIVER_3PARTY_INSTANT;

  // Danh sách ĐVVC cho cả 2 dropdown "Chuyển phát"/"Giao hàng hoả tốc" — lọc theo `carrierMethods`,
  // giống cách AdditionalDeliver ở màn giỏ hàng xử lý. Tự tải ngay khi PTVC đang chọn cần ĐVVC (đủ
  // hiện tên provider đã chọn sẵn), đồng thời cho gọi lại khi user chủ động mở dropdown.
  const { fetchShippingProvider, data: dataShippingProvider } = useGetShippingProviderLazy();

  useEffect(() => {
    if (visible && needsProviderOptions) {
      fetchShippingProvider();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, needsProviderOptions]);

  const isInstantProvider = (provider) => provider?.carrierMethods?.includes(CARRIER_METHOD.INSTANT);
  // `ShippingProvider.fee` — field trực tiếp trên `list`, khác `getCartServiceFee(cartID)`.
  const buildProviderOptions = (providers) =>
    providers?.map((provider) => ({
      value: provider?.id,
      label: provider?.name,
      carrierMethod: provider?.carrierMethods?.[0],
      fee: provider?.fee,
    })) ?? [];

  const deliver3PartyOptions = buildProviderOptions(
    dataShippingProvider?.filter((provider) => !isInstantProvider(provider))
  );
  const instantProviderOptions = buildProviderOptions(dataShippingProvider?.filter(isInstantProvider));

  const shippingTypeOptions = useMemo(() => getShippingTypeOptions(shippingTypes), [shippingTypes]);

  const providerOptions =
    shippingType === SHIPPING_TYPE.DELIVER_3PARTY_INSTANT ? instantProviderOptions : deliver3PartyOptions;

  // Nạp lại giá trị của đơn mỗi lần mở pop up (mở lại sau khi Huỷ phải thấy dữ liệu cũ, không
  // phải lựa chọn dở dang lần trước).
  useEffect(() => {
    if (!visible) return;

    const config = order?.shippingConfiguration;

    setShippingType(order?.shippingType);
    setSelectedPickup({
      warehouseID: order?.pickupInfo?.warehouseID,
      receiverID: order?.pickupInfo?.receiver?.id,
      warehouse: order?.pickupInfo?.warehouse,
      receiver: order?.pickupInfo?.receiver,
    });
    form.setFieldsValue({
      shippingType: order?.shippingType,
      shippingConfiguration: {
        busConfig: {
          busName: config?.busConfig?.busName,
          location: config?.busConfig?.location,
          telephone: config?.busConfig?.telephone,
          busStation: config?.busConfig?.busStation,
        },
        deliver3PartyConfig: { providerID: config?.deliver3PartyConfig?.provider?.id },
        urbanConfig: { fee: config?.urbanConfig?.fee },
      },
    });
    // Deps CHỈ gồm 2 thứ định danh "lần mở popup này". Đừng thêm `order?.shippingType` (onRefetch ở
    // catch cập nhật `order` khi popup còn mở, resync sẽ xoá lựa chọn user vừa chọn) và đừng thêm
    // `dataShippingProvider` (list ĐVVC tải xong sẽ kéo `shippingType` ngược về giá trị cũ, khiến
    // user phải bấm 2 lần mới chọn được PTVC cần ĐVVC).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, order?.id]);

  const onChangeShippingType = (value) => {
    setShippingType(value);
    // Đổi PTVC thì ĐVVC cũ không còn ý nghĩa — xoá để buộc chọn lại. Dùng `setFields` (set đúng 1
    // field theo path) thay vì `setFieldsValue` với object lồng nhau — cách đó GHI ĐÈ toàn bộ
    // `shippingConfiguration` hiện tại (không deep-merge), xoá mất luôn `busConfig` đã nhập.
    form.setFields([{ name: ["shippingConfiguration", "deliver3PartyConfig", "providerID"], value: undefined }]);
  };

  const isPickupReady = Boolean(selectedPickup?.warehouseID && selectedPickup?.receiverID);

  /** Gom form + state cục bộ thành `ChangeShippingMethodInput` */
  const buildShippingConfig = (values) => {
    switch (shippingType) {
      case SHIPPING_TYPE.BUS:
        return { busConfig: { ...values?.shippingConfiguration?.busConfig } };
      case SHIPPING_TYPE.DELIVER_3PARTY:
      case SHIPPING_TYPE.DELIVER_3PARTY_INSTANT: {
        const selectedValue = values?.shippingConfiguration?.deliver3PartyConfig?.providerID;
        const selectedOption = providerOptions?.find((option) => option?.value === selectedValue);
        return {
          deliver3PartyConfig: {
            providerID: `${selectedValue}`.split(" - ")[0],
            carrierMethod:
              selectedOption?.carrierMethod ??
              (shippingType === SHIPPING_TYPE.DELIVER_3PARTY_INSTANT ? CARRIER_METHOD.INSTANT : undefined),
            name: selectedOption?.label,
            fee: selectedOption?.fee,
          },
        };
      }
      case SHIPPING_TYPE.URBAN_COD:
      case SHIPPING_TYPE.URBAN_NOT_COD:
        return { urbanCODConfig: { fee: values?.shippingConfiguration?.urbanConfig?.fee } };
      default:
        // PICK_UP_COUNTER không có nhánh nào trong `ShippingConfigInput` — kho/người nhận gửi bằng
        // `warehouseID`/`pickupReceiverID` ở cấp ngoài cùng của input (xem `handleConfirm`).
        return undefined;
    }
  };

  const handleConfirm = async () => {
    try {
      const values = await form.validateFields();
      const isPickUpCounter = shippingType === SHIPPING_TYPE.PICK_UP_COUNTER;
      if (isPickUpCounter && !isPickupReady) {
        notify.warning({ message: t("v2.order.detail.modalChangeShippingMethod.blankPickupInfo") });
        return;
      }

      setSubmitting(true);
      await handleChangeShippingMethod({
        orderID: order?.id,
        shippingType,
        shippingConfig: buildShippingConfig(values),
        // Kho/người nhận nằm THẲNG trên `ChangeShippingMethodInput`, không nằm trong `shippingConfig`.
        ...(isPickUpCounter && {
          warehouseID: selectedPickup?.warehouseID,
          pickupReceiverID: selectedPickup?.receiverID,
        }),
      });

      notify.success({ message: t("v2.order.detail.modalChangeShippingMethod.changeShippingMethodSuccess") });
      onClose?.();
      // Mutation trả `Boolean!`, không trả đơn mới → bắt buộc refetch để cập nhật phụ phí vận
      // hành / tổng tiền trên khối Hoá đơn.
      await onRefetch?.();
    } catch (error) {
      if (error?.errorFields) return; // lỗi validate của antd, đã hiện dưới field
      notify.error({
        message: t("v2.order.detail.modalChangeShippingMethod.changeShippingMethodError"),
        description: error?.message,
      });
      // BE có thể đã ghi nhận một phần trước khi lỗi — refetch để UI không lệch với dữ liệu thật.
      await onRefetch?.();
    } finally {
      setSubmitting(false);
    }
  };

  return {
    form,
    shippingType,
    shippingTypeOptions,
    providerOptions,
    fetchShippingProvider,
    onChangeShippingType,
    selectedPickup,
    setSelectedPickup,
    submitting,
    handleConfirm,
  };
};

export default useModalChangeShippingMethod;
